// ============================================================
// utils/catalogStorage.ts
// Caché de catálogos estáticos en localStorage con TTL.
// Se pobla al hacer login y se limpia al hacer logout.
// ============================================================

import { AñoEscolar, Grado, Seccion, Curso, RolItem } from '../types/usuario.types';

const KEYS = {
  ANIOS:        'catalog_anios_escolares',
  ANIO_ACTIVO:  'catalog_anio_activo',
  GRADOS:       'catalog_grados',
  SECCIONES:    'catalog_secciones',
  CURSOS:       'catalog_cursos',
  ROLES:        'catalog_roles',
  SELLO:        'config_sello_ie',
} as const;

// TTL por catálogo (ms). Pasado este tiempo, get devuelve null → se refetcha.
const TTL: Record<string, number> = {
  [KEYS.ANIOS]:     60 * 60 * 1000,  // 1 hora
  [KEYS.GRADOS]:    60 * 60 * 1000,  // 1 hora
  [KEYS.SECCIONES]: 30 * 60 * 1000,  // 30 min (cambia al añadir secciones)
  [KEYS.CURSOS]:    60 * 60 * 1000,  // 1 hora
  [KEYS.ROLES]:     120 * 60 * 1000, // 2 horas
};

function tsKey(key: string): string { return `${key}_ts`; }

function save<T>(key: string, data: T[]): void {
  localStorage.setItem(key, JSON.stringify(data));
  localStorage.setItem(tsKey(key), String(Date.now()));
}

function load<T>(key: string): T[] | null {
  const ttl = TTL[key];
  if (ttl !== undefined) {
    const ts = Number(localStorage.getItem(tsKey(key)) ?? 0);
    if (ts && Date.now() - ts > ttl) {
      // Expirado — limpiar y forzar refetch
      localStorage.removeItem(key);
      localStorage.removeItem(tsKey(key));
      return null;
    }
  }
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as T[] : null;
  } catch {
    return null;
  }
}

export const catalogStorage = {

  // ── Años escolares ────────────────────────────────────────
  saveAñosEscolares(data: AñoEscolar[]): void  { save(KEYS.ANIOS, data); },
  getAñosEscolares(): AñoEscolar[] | null       { return load<AñoEscolar>(KEYS.ANIOS); },

  // ── Grados ────────────────────────────────────────────────
  saveGrados(data: Grado[]): void  { save(KEYS.GRADOS, data); },
  getGrados(): Grado[] | null       { return load<Grado>(KEYS.GRADOS); },

  // ── Secciones ─────────────────────────────────────────────
  saveSecciones(data: Seccion[]): void  { save(KEYS.SECCIONES, data); },
  getSecciones(): Seccion[] | null       { return load<Seccion>(KEYS.SECCIONES); },
  clearSecciones(): void                 {
    localStorage.removeItem(KEYS.SECCIONES);
    localStorage.removeItem(tsKey(KEYS.SECCIONES));
  },

  // ── Cursos ────────────────────────────────────────────────
  saveCursos(data: Curso[]): void  { save(KEYS.CURSOS, data); },
  getCursos(): Curso[] | null       { return load<Curso>(KEYS.CURSOS); },

  // ── Roles ─────────────────────────────────────────────────
  saveRoles(data: RolItem[]): void  { save(KEYS.ROLES, data); },
  getRoles(): RolItem[] | null       { return load<RolItem>(KEYS.ROLES); },

  /** Devuelve el idRol para un nombre de rol, con fallback al mapa estático. */
  getIdRol(nombre: string, fallback: number): number {
    const roles = load<RolItem>(KEYS.ROLES);
    return roles?.find(r => r.nombre === nombre)?.idRol ?? fallback;
  },

  // ── Año escolar activo (viene del bootstrap) ──────────────
  saveAnioEscolarActivo(data: AñoEscolar): void {
    localStorage.setItem(KEYS.ANIO_ACTIVO, JSON.stringify(data));
  },
  getAnioEscolarActivo(): AñoEscolar | null {
    const raw = localStorage.getItem(KEYS.ANIO_ACTIVO);
    if (!raw) return null;
    try { return JSON.parse(raw) as AñoEscolar; } catch { return null; }
  },

  // ── Sello de la IE (IE_SELLO_BASE64) ─────────────────────
  saveSelloBase64(val: string): void { localStorage.setItem(KEYS.SELLO, val); },
  getSelloBase64(): string | null    { return localStorage.getItem(KEYS.SELLO); },
  clearSelloBase64(): void           { localStorage.removeItem(KEYS.SELLO); },

  // ── Limpieza total (se llama en logout) ───────────────────
  clearAll(): void {
    Object.values(KEYS).forEach(k => {
      localStorage.removeItem(k);
      localStorage.removeItem(tsKey(k));
    });
  },

  // ── ¿Están los catálogos del bootstrap cargados? ──────────
  // No incluye ANIOS porque el bootstrap solo trae el año activo,
  // los años completos los carga precargarCatalogos tras el login.
  isLoaded(): boolean {
    return (
      load(KEYS.GRADOS)    !== null &&
      load(KEYS.SECCIONES) !== null &&
      load(KEYS.ROLES)     !== null
    );
  },
};
